import { useMemo, useState } from 'react'
import { Edit, Trash2 } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'
import { CategoryForm } from '../components/CategoryForm'
import { DeleteCategory } from '../components/DeleteCategory'
import { CategoryCard } from '../components/CategoryCard'
import type { categoryFilterSchema } from '@/schemas/filterSchemas'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { useCleanQueryStates, usePagination } from '@/hooks'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/custom/badge'
import { usePermissions } from '@/utils/permissions'

export default function Categories() {
  const permissions = usePermissions()

  const [open, setOpen] = useState({ add: false, edit: false, delete: false })
  const [categoryId, setCategoryId] = useState<string | null>(null)

  const [query, setQuery] = useCleanQueryStates<typeof categoryFilterSchema>(
    '/authed/inventory/categories',
  )
  const categories = useInventoryStore((state) => state.categories)

  const filtered = useMemo(
    () => categories.sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filtered,
    pageSize: 10,
  })

  return (
    <div className="space-y-3">
      <ListPageHeader
        type="categories"
        searchValue={query.search}
        onSearchChange={(value) => {
          setQuery({ search: value })
          goToFirstPage()
        }}
        initialFilters={query}
        onFilter={(filters) => {
          setQuery(filters)
          goToFirstPage()
        }}
        isFormOpen={open.add}
        onFormOpen={() => setOpen((o) => ({ ...o, add: true }))}
        onFormClose={() => setOpen((o) => ({ ...o, add: false }))}
        showAddButton={true}
      />

      <div className="hidden lg:block overflow-hidden rounded-md border bg-white">
        <Table>
          <THead>
            <TR>
              <TH>Category</TH>
              <TH>Description</TH>
              <TH>Status</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((category) => (
              <TR key={category._id}>
                <TD>{category.name}</TD>
                <TD className="text-neutral-600">{category.description || '—'}</TD>
                <TD>
                  <Badge>{category.active ? 'Active' : 'Inactive'}</Badge>
                </TD>
                <TD className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCategoryId(category._id)
                        setOpen((o) => ({ ...o, edit: true }))
                      }}
                    >
                      <Edit className="h-4 w-4" /> Edit
                    </Button>

                    {permissions.canDeleteInventoryCategory() && (
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setCategoryId(category._id)
                          setOpen((o) => ({ ...o, delete: true }))
                        }}
                      >
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    )}
                  </div>
                </TD>
              </TR>
            ))}
            {rows.length === 0 && (
              <TR>
                <TD className="p-6 text-center text-neutral-500" colSpan={4}>
                  No categories found
                </TD>
              </TR>
            )}
          </TBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <ScrollArea className="lg:hidden h-[calc(100vh-170px)]">
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
              No categories found
            </div>
          ) : (
            filtered.map((category) => {
              return (
                <CategoryCard
                  key={category._id}
                  category={category}
                  onEdit={(id) => {
                    setOpen((o) => ({ ...o, edit: true }))
                    setCategoryId(id)
                  }}
                  onDelete={(id) => {
                    setCategoryId(id)
                    setOpen((o) => ({ ...o, delete: true }))
                  }}
                  onDetail={(id) => {
                    setCategoryId(id)
                    setOpen((o) => ({ ...o, detail: true }))
                  }}
                />
              )
            })
          )}
        </div>
      </ScrollArea>

      <div className="hidden lg:block">
        <PaginationWrapper
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      </div>

      <CategoryForm
        id={categoryId}
        type="edit"
        open={open.edit}
        onClose={() => {
          setCategoryId(null)
          setOpen((o) => ({ ...o, edit: false }))
        }}
      />

      <DeleteCategory
        id={categoryId}
        open={open.delete}
        onClose={() => {
          setCategoryId(null)
          setOpen((o) => ({ ...o, delete: false }))
        }}
      />
    </div>
  )
}
